import { Box, Flex, Grid } from '@chakra-ui/react'
import {
  CalendarDateRangeIcon,
  DocumentPlusIcon,
  PencilIcon,
  XCircleIcon
} from '@heroicons/react/16/solid'
import { Modal, Tooltip } from '@sequencehq/core-components'
import {
  GreyGrey10,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  Lato12Regular,
  Lato13Bold
} from '@sequencehq/design-tokens'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useAtom } from 'jotai'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { modalScreenAtom } from '../atoms/screens.atom'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { parseBillingSchedulePhaseDuration } from 'Cube/communication/external/billingSchedule.api.v1/adapters/billingSchedule.adapters.phases.in'
import { parse } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'

const UpdateSelectionItem = (props: {
  icon: ReactNode
  title: string
  description: string
  onClick?: () => void
  disabled?: boolean
  disabledReason?: string
}) => {
  return (
    <Tooltip isDisabled={!props.disabled} label={props.disabledReason}>
      <Flex
        alignItems="center"
        justifyContent="center"
        backgroundColor={GreyWhite}
        onClick={props.disabled ? () => null : props.onClick}
        cursor={props.disabled ? 'default' : 'pointer'}
        _hover={
          props.disabled
            ? {}
            : {
                '& .content': {
                  transform: 'scale(1.05)'
                },
                '& .icon': {
                  backgroundColor: GreyGrey30
                },
                backgroundColor: GreyGrey10
              }
        }
        userSelect="none"
        height="100%"
        width="100%"
      >
        <Flex
          className="content"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          transition="transform 0.3s ease-in-out"
          opacity={props.disabled ? 0.5 : 1}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            height="32px"
            width="32px"
            borderRadius="6px"
            backgroundColor={GreyGrey20}
            marginBottom="16px"
            color={GreyGrey70}
            className="icon"
            transition="background-color 0.3s ease-in-out"
          >
            {props.icon}
          </Flex>
          <Box color={GreyGrey80} {...Lato13Bold}>
            {props.title}
          </Box>
          <Box color={GreyGrey60} {...Lato12Regular}>
            {props.description}
          </Box>
        </Flex>
      </Flex>
    </Tooltip>
  )
}

const iconProps = {
  height: '16px',
  width: '16px'
}

const useAvailableShortcuts = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const phaseDurations = data.billingSchedule.phases.map(phase => {
        const startDate = parse(phase.startDate, 'yyyy-MM-dd', new Date())
        const endDate = phase.endDate
          ? parse(phase.endDate, 'yyyy-MM-dd', new Date())
          : undefined
        const duration = parseBillingSchedulePhaseDuration({
          startDate,
          endDate
        })

        return duration
      })

      const finalPhaseDuration = phaseDurations.at(-1)

      if (!finalPhaseDuration) {
        return {
          finalPhase: {
            isOpenEnded: false,
            isMilestone: false
          },
          hasMilestonePhase: false
        }
      }

      return {
        finalPhase: {
          isOpenEnded: finalPhaseDuration === 'OPEN_ENDED',
          isMilestone: finalPhaseDuration === 'MILESTONE'
        },
        hasMilestonePhase: Boolean(phaseDurations.find(d => d === 'MILESTONE')),
        status: data.billingSchedule.status,
        isArchived: Boolean(data.billingSchedule.archivedAt)
      }
    }
  })

  return {
    renew: match({
      finalPhase: billingScheduleAggregateQuery.data?.finalPhase,
      hasMilestonePhase: billingScheduleAggregateQuery.data?.hasMilestonePhase
    })
      .with({ finalPhase: { isOpenEnded: true } }, () => ({
        disabled: true,
        reason: "Can't renew an open ended schedule."
      }))
      .with({ finalPhase: { isMilestone: true } }, () => ({
        disabled: true,
        reason: "Can't renew a schedule when final phase is a milestone phase."
      }))
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      })),
    cancel: match({
      isArchived: billingScheduleAggregateQuery.data?.isArchived,
      status: billingScheduleAggregateQuery.data?.status
    })
      .with(
        {
          isArchived: true
        },
        () => ({ disabled: true, reason: `Can't cancel an archived schedule` })
      )
      .with(
        { status: 'DRAFT' },
        { status: 'COMPLETED' },
        { status: 'CANCELLED' },
        { status: 'SUSPENDED' },
        /**
         * TODO: Better tailor the messages for each status
         */
        st => ({
          disabled: true,
          reason: `Can't cancel a schedule with the status ${st.status}`
        })
      )
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      })),
    upgrade: match({
      finalPhase: billingScheduleAggregateQuery.data?.finalPhase,
      hasMilestonePhase: billingScheduleAggregateQuery.data?.hasMilestonePhase
    })
      .with({ finalPhase: { isOpenEnded: true } }, () => ({
        disabled: true,
        reason: "Can't upgrade an open ended schedule."
      }))
      .with({ hasMilestonePhase: true }, () => ({
        disabled: true,
        reason: "Can't upgrade a schedule with a milestone phase."
      }))
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      }))
  }
}

export const ShortcutSelectionScreen = () => {
  const billingScheduleId = useBillingScheduleId()
  const [, setScreen] = useAtom(modalScreenAtom)
  const shortcuts = useAvailableShortcuts()

  return (
    <Modal.Content>
      <Grid
        gridTemplateRows="166px 166px"
        gridTemplateColumns="220px 220px"
        gridColumnGap="1px"
        gridRowGap="1px"
        backgroundColor={GreyGrey30}
        borderRadius="8px"
        border={`1px solid ${GreyGrey30}`}
        overflow="hidden"
      >
        <UpdateSelectionItem
          icon={<DocumentPlusIcon {...iconProps} />}
          onClick={() => setScreen('upgrade')}
          title="Upgrade"
          description="Add products or change pricing"
          disabled={shortcuts.upgrade.disabled}
          disabledReason={shortcuts.upgrade.reason}
        />
        <UpdateSelectionItem
          icon={<CalendarDateRangeIcon {...iconProps} />}
          onClick={() => setScreen('renew')}
          title="Renew"
          description="Renew this schedule"
          disabled={shortcuts.renew.disabled}
          disabledReason={shortcuts.renew.reason}
        />
        <UpdateSelectionItem
          icon={<XCircleIcon {...iconProps} />}
          onClick={() => setScreen('cancel')}
          title="Cancel"
          description="End this schedule"
          disabled={shortcuts.cancel.disabled}
          disabledReason={shortcuts.cancel.reason}
        />
        <Link to={`/billing-schedules/${billingScheduleId}/edit`}>
          <UpdateSelectionItem
            icon={<PencilIcon {...iconProps} />}
            title="Other"
            description="Make custom changes"
          />
        </Link>
      </Grid>
    </Modal.Content>
  )
}
